/**
 * Vendored as-is from https://github.com/dashed/shallowequal
 *
 * ===============================================================================
 *
 * MIT License
 *
 * Copyright (c) 2017 Alberto Leal <mailforalberto@gmail.com> (github.com/dashed)
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 * ===============================================================================
 *
 * Docs:
 *
 * shallowEqual is like lodash's isEqual (v3.10.1) but for shallow (strict) equal.
 *
 *     shallowEqual(value, other, [customizer], [thisArg])
 *
 * Performs a shallow equality comparison between two values (i.e. value and
 * other) to determine if they are equivalent.
 *
 * The equality check returns `true` if value and other are already strictly
 * equal, OR when all the following are true:
 *
 *   - `value` and `other` are both objects with the same keys
 *   - For each key, the value in `value` and `other` are **strictly equal** (===)
 *
 * If customizer (expected to be a function) is provided it is invoked to
 * compare values. If customizer returns undefined (i.e. `void 0`), then
 * comparisons are handled by the shallowEqual function instead.
 *
 * The customizer is bound to thisArg and invoked with three arguments:
 *   (value, other, key).
 */
export const shallowEqual = (objA, objB, compare, compareContext) => {
  var ret = compare ? compare.call(compareContext, objA, objB) : void 0;

  if (ret !== void 0) {
    return !!ret;
  }

  if (objA === objB) {
    return true;
  }

  if (typeof objA !== 'object' || !objA || typeof objB !== 'object' || !objB) {
    return false;
  }

  var keysA = Object.keys(objA);
  var keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  var bHasOwnProperty = Object.prototype.hasOwnProperty.bind(objB);

  // Test for A's keys different from B.
  for (var idx = 0; idx < keysA.length; idx++) {
    var key = keysA[idx];

    if (!bHasOwnProperty(key)) {
      return false;
    }

    var valueA = objA[key];
    var valueB = objB[key];

    ret = compare ? compare.call(compareContext, valueA, valueB, key) : void 0;

    if (ret === false || (ret === void 0 && valueA !== valueB)) {
      return false;
    }
  }

  return true;
};
